import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import '../styles.css';
import { API_URL } from '../config';

function Help() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    purpose: 'question'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API_URL}/api/subscription/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsSubscriber(response.data.isPremium);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      await axios.post(`${API_URL}/api/contact`, contactForm);
      setSubmitStatus('success');
      setContactForm({ name: '', email: '', subject: '', message: '', purpose: 'question' });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Contact form error:', error);
      // Show more specific error message
      if (error.response?.data?.error) {
        setSubmitStatus('error');
        console.error('Server error:', error.response.data.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Layout>
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>
        How True Devotion Works
      </h1>

      {/* Introduction */}
      <div style={{ marginBottom: '40px', textAlign: 'center' }}>
        <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#666' }}>
          True Devotion helps couples build deeper connections through thoughtful daily questions and meaningful conversations.
        </p>
      </div>

      {/* How It Works Section */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ color: '#333', marginBottom: '20px' }}>How It Works</h2>
        
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#007bff', marginBottom: '10px' }}>1. Daily Questions</h3>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              Each day, you'll receive a thoughtful question designed to spark meaningful conversations with your partner. 
              These questions help you discover new things about each other and strengthen your connection.
            </p>
          </div>

          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#007bff', marginBottom: '10px' }}>2. Answer & Connect</h3>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              Answer the daily question and share your thoughts with your partner. Your answers are stored securely 
              and can be revisited anytime to remember what matters most to each other.
            </p>
          </div>

          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#007bff', marginBottom: '10px' }}>3. Build Understanding</h3>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              Over time, you'll build a rich history of your partner's thoughts, dreams, and preferences. 
              This understanding helps you be more thoughtful in your relationship and daily interactions.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ color: '#333', marginBottom: '20px' }}>Features</h2>
        
        <div style={{ display: 'grid', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#28a745', fontSize: '20px' }}>✓</span>
            <div>
              <strong>Daily Questions</strong> - Thoughtful prompts delivered daily to spark meaningful conversations
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#28a745', fontSize: '20px' }}>✓</span>
            <div>
              <strong>Answer History</strong> - Browse and search through all your partner's responses
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#28a745', fontSize: '20px' }}>✓</span>
            <div>
              <strong>Skip Questions</strong> - Skip up to 7 questions per day if they don't feel right
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#28a745', fontSize: '20px' }}>✓</span>
            <div>
              <strong>Complete Privacy</strong> - All answers encrypted with personal keys, completely private
            </div>
          </div>
        </div>
      </div>

      {/* Premium Features */}
      <div style={{ 
        marginBottom: '40px', 
        padding: '25px', 
        backgroundColor: '#e3f2fd', 
        borderRadius: '8px',
        border: '1px solid #bbdefb'
      }}>
        <h2 style={{ color: '#1976d2', marginBottom: '20px' }}>Premium Features</h2>
        
        <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#1976d2', fontSize: '20px' }}>⭐</span>
            <div>
              <strong>AI Relationship Advisor</strong> - Get personalized insights and suggestions based on your partner's answers
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#1976d2', fontSize: '20px' }}>⭐</span>
            <div>
              <strong>Custom Questions</strong> - Create your own questions tailored to your relationship
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#1976d2', fontSize: '20px' }}>⭐</span>
            <div>
              <strong>Advanced Search</strong> - Find specific answers and patterns in your conversation history
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ color: '#1976d2', fontSize: '20px' }}>⭐</span>
            <div>
              <strong>Edit & Delete</strong> - Manage your conversation history and custom questions
            </div>
          </div>
        </div>

        {!isSubscriber && (
          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={() => window.location.href = '/upgrade'}
              style={{
                padding: '12px 24px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Upgrade to Premium
            </button>
          </div>
        )}
      </div>

      {/* Benefits Section */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ color: '#333', marginBottom: '20px' }}>Why True Devotion Works</h2>
        
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>Deeper Connection</h3>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              Regular meaningful conversations help you understand your partner on a deeper level, 
              strengthening your emotional bond and creating lasting intimacy.
            </p>
          </div>

          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>Better Communication</h3>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              Thoughtful questions naturally lead to better conversations, helping you both 
              express your thoughts, feelings, and needs more clearly.
            </p>
          </div>

          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>Proactive Love</h3>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              Show your partner you're invested in understanding them better. 
              True Devotion helps you be more thoughtful and considerate in your relationship.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div id="contact" style={{ 
        padding: '30px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h2 style={{ color: '#333', marginBottom: '20px', textAlign: 'center' }}>Get in Touch</h2>
        
        {submitStatus === 'success' && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            borderRadius: '6px', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Thank you! Your message has been sent successfully.
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '6px', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Sorry, there was an error sending your message. Please try again.
          </div>
        )}

        <form onSubmit={handleContactSubmit} style={{ display: 'grid', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Name (Optional)
              </label>
              <input
                type="text"
                name="name"
                value={contactForm.name}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={contactForm.email}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Subject *
            </label>
            <input
              type="text"
              name="subject"
              value={contactForm.subject}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Purpose of Contact *
            </label>
            <select
              name="purpose"
              value={contactForm.purpose}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              <option value="question">General Question</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="idea">Suggestion/Idea</option>
              <option value="support">Technical Support</option>
              <option value="feedback">Feedback</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Message *
            </label>
            <textarea
              name="message"
              value={contactForm.message}
              onChange={handleInputChange}
              required
              rows="5"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                resize: 'vertical'
              }}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-form-submit"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </Layout>
  );
}

export default Help;
